package com.daytimegaming.ourdailyfinances.presentation.dashboard

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.Account
import com.daytimegaming.ourdailyfinances.domain.model.DashboardAccount
import com.daytimegaming.ourdailyfinances.domain.model.DashboardDetail
import com.daytimegaming.ourdailyfinances.domain.model.RealEstateHolding
import com.daytimegaming.ourdailyfinances.domain.model.User
import com.daytimegaming.ourdailyfinances.domain.usecase.AccountUseCase
import com.daytimegaming.ourdailyfinances.domain.usecase.AuthUseCase
import com.daytimegaming.ourdailyfinances.domain.usecase.DashboardUseCase
import com.daytimegaming.ourdailyfinances.domain.usecase.RealEstateUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class ConfigureDashboardState(
    val dashboardId: String = "",
    val dashboardDetail: DashboardDetail? = null,
    val currentUser: User? = null,
    val availableAccounts: List<Account> = emptyList(),
    val availableRealEstateHoldings: List<RealEstateHolding> = emptyList(),
    val editedName: String = "",
    val selectedAccounts: List<DashboardAccount> = emptyList(),
    val selectedRealEstateHoldings: List<RealEstateHolding> = emptyList(),
    val isLoading: Boolean = false,
    val isActionLoading: Boolean = false,
    val error: String? = null,
    val hasLeft: Boolean = false,
    val hasDeleted: Boolean = false,
    val hasChanges: Boolean = false,
    val isUpdateSuccess: Boolean = false
)

class ConfigureDashboardViewModel(
    private val dashboardId: String,
    private val dashboardUseCase: DashboardUseCase,
    private val accountUseCase: AccountUseCase,
    private val authUseCase: AuthUseCase,
    private val realEstateUseCase: RealEstateUseCase,
) : ViewModel() {

    private val _state = MutableStateFlow(ConfigureDashboardState(dashboardId = dashboardId))
    val state = _state.asStateFlow()

    init {
        loadDashboardDetail()
        loadCurrentUser()
        loadAvailableAccounts()
        loadAvailableRealEstateHoldings()
    }

    fun loadDashboardDetail() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            dashboardUseCase.GetDashboardDetail(dashboardId).collect { response ->
                when (response) {
                    is Response.Success -> {
                        _state.update { oldState ->
                            val detail = response.data
                            val isFirstLoad = oldState.dashboardDetail == null
                            oldState.copy(
                                isLoading = false,
                                dashboardDetail = detail,
                                editedName = if (isFirstLoad) detail.name else oldState.editedName,
                                selectedAccounts = if (isFirstLoad) detail.accounts else oldState.selectedAccounts,
                                selectedRealEstateHoldings = if (isFirstLoad) detail.realEstateHoldings else oldState.selectedRealEstateHoldings,
                                hasChanges = if (isFirstLoad) false else oldState.hasChanges
                            )
                        }
                    }
                    is Response.Error -> {
                        _state.update { it.copy(isLoading = false, error = response.message) }
                    }
                    is Response.Loading -> {
                        _state.update { it.copy(isLoading = true) }
                    }
                }
            }
        }
    }

    private fun loadCurrentUser() {
        viewModelScope.launch {
            authUseCase.GetCurrentUser().collect { user ->
                _state.update { it.copy(currentUser = user) }
            }
        }
    }

    private fun loadAvailableAccounts() {
        viewModelScope.launch {
            accountUseCase.GetAccounts().collect { response ->
                when (response) {
                    is Response.Success -> {
                        _state.update { it.copy(availableAccounts = response.data) }
                    }
                    is Response.Error -> {
                        _state.update { it.copy(error = response.message) }
                    }
                    else -> {}
                }
            }
        }
    }

    private fun loadAvailableRealEstateHoldings() {
        viewModelScope.launch {
            realEstateUseCase.GetRealEstateHoldings().collect { response ->
                when (response) {
                    is Response.Success -> {
                        _state.update { it.copy(availableRealEstateHoldings = response.data) }
                    }
                    is Response.Error -> {
                        _state.update { it.copy(error = response.message) }
                    }
                    else -> {}
                }
            }
        }
    }

    fun onNameChange(newName: String) {
        _state.update { state ->
            val hasChanges = calculateHasChanges(newName, state.selectedAccounts, state.selectedRealEstateHoldings, state.dashboardDetail)
            state.copy(editedName = newName, hasChanges = hasChanges)
        }
    }

    fun selectAccount(account: Account) {
        _state.update { state ->
            val alreadySelected = state.selectedAccounts.any { it.accountId == account.accountId }
            if (alreadySelected) {
                state
            } else {
                val newAccount = DashboardAccount(
                    accountId = account.accountId,
                    name = account.name,
                    officialName = account.officialName,
                    type = account.type,
                    subtype = account.subtype,
                    currentBalance = account.currentBalance,
                    availableBalance = account.availableBalance,
                    isoCurrencyCode = account.isoCurrencyCode,
                    addedByUserId = state.currentUser?.uid ?: "",
                    itemId = account.itemId,
                    institutionName = account.institutionName
                )
                val newAccounts = state.selectedAccounts + newAccount
                val hasChanges = calculateHasChanges(state.editedName, newAccounts, state.selectedRealEstateHoldings, state.dashboardDetail)
                state.copy(selectedAccounts = newAccounts, hasChanges = hasChanges)
            }
        }
    }

    fun removeAccount(accountId: String) {
        _state.update { state ->
            val newAccounts = state.selectedAccounts.filter { it.accountId != accountId }
            val hasChanges = calculateHasChanges(state.editedName, newAccounts, state.selectedRealEstateHoldings, state.dashboardDetail)
            state.copy(selectedAccounts = newAccounts, hasChanges = hasChanges)
        }
    }

    fun selectRealEstateHolding(holding: RealEstateHolding) {
        _state.update { state ->
            val alreadySelected = state.selectedRealEstateHoldings.any { it.realEstateHoldingId == holding.realEstateHoldingId }
            if (alreadySelected) {
                state
            } else {
                val newHoldings = state.selectedRealEstateHoldings + holding
                val hasChanges = calculateHasChanges(state.editedName, state.selectedAccounts, newHoldings, state.dashboardDetail)
                state.copy(selectedRealEstateHoldings = newHoldings, hasChanges = hasChanges)
            }
        }
    }

    fun removeRealEstateHolding(holdingId: String) {
        _state.update { state ->
            val newHoldings = state.selectedRealEstateHoldings.filter { it.realEstateHoldingId != holdingId }
            val hasChanges = calculateHasChanges(state.editedName, state.selectedAccounts, newHoldings, state.dashboardDetail)
            state.copy(selectedRealEstateHoldings = newHoldings, hasChanges = hasChanges)
        }
    }

    private fun calculateHasChanges(
        name: String,
        accounts: List<DashboardAccount>,
        holdings: List<RealEstateHolding>,
        detail: DashboardDetail?
    ): Boolean {
        if (detail == null) return false
        val nameChanged = name != detail.name
        val originalAccountIds = detail.accounts.map { it.accountId }.toSet()
        val currentAccountIds = accounts.map { it.accountId }.toSet()
        val accountsChanged = originalAccountIds != currentAccountIds

        val originalHoldingIds = detail.realEstateHoldings.map { it.realEstateHoldingId }.toSet()
        val currentHoldingIds = holdings.map { it.realEstateHoldingId }.toSet()
        val holdingsChanged = originalHoldingIds != currentHoldingIds

        return nameChanged || accountsChanged || holdingsChanged
    }

    fun updateDashboard() {
        val currentState = _state.value
        val detail = currentState.dashboardDetail ?: return

        viewModelScope.launch {
            _state.update { it.copy(isActionLoading = true, error = null) }
            try {
                // 1. Commit name change if modified
                if (currentState.editedName != detail.name) {
                    val renameResponse = dashboardUseCase.UpdateDashboard(dashboardId, currentState.editedName)
                    if (renameResponse is Response.Error) {
                        _state.update { it.copy(isActionLoading = false, error = renameResponse.message) }
                        return@launch
                    }
                }

                // 2. Commit account changes
                val originalIds = detail.accounts.map { it.accountId }.toSet()
                val currentIds = currentState.selectedAccounts.map { it.accountId }.toSet()

                val toAdd = currentIds - originalIds
                val toRemove = originalIds - currentIds

                // Add new accounts
                for (accountId in toAdd) {
                    val addRes = dashboardUseCase.AddDashboardAccount(dashboardId, accountId)
                    if (addRes is Response.Error) {
                        _state.update { it.copy(isActionLoading = false, error = addRes.message) }
                        return@launch
                    }
                }

                // Remove accounts
                for (accountId in toRemove) {
                    val removeRes = dashboardUseCase.RemoveDashboardAccount(dashboardId, accountId)
                    if (removeRes is Response.Error) {
                        _state.update { it.copy(isActionLoading = false, error = removeRes.message) }
                        return@launch
                    }
                }

                // 3. Commit real estate holding changes
                val originalHoldingIds = detail.realEstateHoldings.map { it.realEstateHoldingId }.toSet()
                val currentHoldingIds = currentState.selectedRealEstateHoldings.map { it.realEstateHoldingId }.toSet()

                val toAddHoldings = currentHoldingIds - originalHoldingIds
                val toRemoveHoldings = originalHoldingIds - currentHoldingIds

                // Add new holdings
                for (holdingId in toAddHoldings) {
                    val addRes = dashboardUseCase.AddDashboardRealEstateHolding(dashboardId, holdingId)
                    if (addRes is Response.Error) {
                        _state.update { it.copy(isActionLoading = false, error = addRes.message) }
                        return@launch
                    }
                }

                // Remove holdings
                for (holdingId in toRemoveHoldings) {
                    val removeRes = dashboardUseCase.RemoveDashboardRealEstateHolding(dashboardId, holdingId)
                    if (removeRes is Response.Error) {
                        _state.update { it.copy(isActionLoading = false, error = removeRes.message) }
                        return@launch
                    }
                }

                _state.update { it.copy(isActionLoading = false, isUpdateSuccess = true) }
                forceReloadDashboardDetail()
            } catch (e: Exception) {
                _state.update { it.copy(isActionLoading = false, error = e.message ?: "Failed to update dashboard") }
            }
        }
    }

    private fun forceReloadDashboardDetail() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            dashboardUseCase.GetDashboardDetail(dashboardId).collect { response ->
                when (response) {
                    is Response.Success -> {
                        _state.update { oldState ->
                            val detail = response.data
                            oldState.copy(
                                isLoading = false,
                                dashboardDetail = detail,
                                editedName = detail.name,
                                selectedAccounts = detail.accounts,
                                selectedRealEstateHoldings = detail.realEstateHoldings,
                                hasChanges = false
                            )
                        }
                    }
                    is Response.Error -> {
                        _state.update { it.copy(isLoading = false, error = response.message) }
                    }
                    is Response.Loading -> {
                        _state.update { it.copy(isLoading = true) }
                    }
                }
            }
        }
    }

    fun createInvite() {
        viewModelScope.launch {
            _state.update { it.copy(isActionLoading = true, error = null) }
            val response = dashboardUseCase.CreateInvite(dashboardId)
            when (response) {
                is Response.Success -> {
                    _state.update { it.copy(isActionLoading = false) }
                    loadDashboardDetail()
                }
                is Response.Error -> {
                    _state.update { it.copy(isActionLoading = false, error = response.message) }
                }
                else -> {}
            }
        }
    }

    fun revokeInvite(code: String) {
        viewModelScope.launch {
            _state.update { it.copy(isActionLoading = true, error = null) }
            val response = dashboardUseCase.RevokeInvite(dashboardId, code)
            when (response) {
                is Response.Success -> {
                    _state.update { it.copy(isActionLoading = false) }
                    loadDashboardDetail()
                }
                is Response.Error -> {
                    _state.update { it.copy(isActionLoading = false, error = response.message) }
                }
                else -> {}
            }
        }
    }

    fun leaveDashboard() {
        viewModelScope.launch {
            _state.update { it.copy(isActionLoading = true, error = null) }
            val response = dashboardUseCase.LeaveDashboard(dashboardId)
            when (response) {
                is Response.Success -> {
                    _state.update { it.copy(isActionLoading = false, hasLeft = true) }
                }
                is Response.Error -> {
                    _state.update { it.copy(isActionLoading = false, error = response.message) }
                }
                else -> {}
            }
        }
    }

    fun deleteDashboard() {
        viewModelScope.launch {
            _state.update { it.copy(isActionLoading = true, error = null) }
            val response = dashboardUseCase.DeleteDashboard(dashboardId)
            when (response) {
                is Response.Success -> {
                    _state.update { it.copy(isActionLoading = false, hasDeleted = true) }
                }
                is Response.Error -> {
                    _state.update { it.copy(isActionLoading = false, error = response.message) }
                }
                else -> {}
            }
        }
    }

    fun clearError() {
        _state.update { it.copy(error = null) }
    }
}
